import { Dropdown, DropdownProps } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormClearErrors,
} from 'react-hook-form';

import ErrorMessage from '../../error-message';
import styles from './DropDownInput.module.css';

export type DropdownInputProps<T extends FieldValues> = Omit<DropdownProps, 'name'> & {
  label?: string;
  clearErrors?: UseFormClearErrors<T>;
  showError?: boolean;
  customIcon?: React.ReactNode;
  customIconPosition?: 'left' | 'right';
} & UseControllerProps<T>;

const DropDownInput = <T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  label,
  className,
  clearErrors,
  showError = true,
  customIcon,
  customIconPosition = 'right',
  ...props
}: DropdownInputProps<T>) => {
  const {
    field: { value, ref, onChange, ...inputProps },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  return (
    <div className='field p-fluid'>
      <span
        className={classNames(
          customIcon ? `p-input-icon-${customIconPosition}` : '',
          'p-float-label',
        )}
      >
        <Dropdown
          id={name}
          value={value}
          ref={ref}
          onChange={(e) => {
            onChange(e.value);
            clearErrors?.(name);
          }}
          {...props}
          {...inputProps}
          optionLabel='name'
          className={classNames(className, {
            'p-invalid': error,
          })}
        />
        {customIcon && <>{customIcon}</>}
        {label && <label htmlFor={name}>{label}</label>}
      </span>

      {/* Hidden input to enable focus & auto-scroll on error */}
      <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={ref} />

      {showError && error?.message && (
        <div className={classNames(styles['error-message-box'])}>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default DropDownInput;
